/**
 * @todo Remove this once the new recipe object is implemented.
 */
import Tokens from '@builder-legacy/tokens';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
const { isEmpty, isDefined, htmlEntities, supportsRegExLookbehind } = _uo.utility;

// Import style for web component
import styles from './web-component-style.scss';

// Translatable strings
const strings = UncannyAutomator._core.i18n.components.item;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-sentence' )
export class AutomatorBuilderItemSentence extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// The unique ID of the item
	@property( { type: String, attribute: 'item-id' } ) accessor itemID;

	// The item dynamic sentence
	@property( { type: String, attribute: 'item-sentence' } ) accessor sentence;

	// The fields
	@property( { type: Object, attribute: false } ) accessor fields;

	// Check whether the pills/dynamic parts should be clickable
	@property( { type: Boolean, attribute: 'not-interactive-pills' } ) accessor notInteractivePills = false;

	// Returns the child element that contains the sentence
	@query( '.sentence' ) accessor $sentence;

	// eslint-disable-next-line require-jsdoc
	render() {
		return this._templateSentence;
	}

	/**
	 * Gets the tokens instance
	 * @todo Remove this once the new recipe object is implemented.
	 */
	connectedCallback() {
		super.connectedCallback();

		/**
		 * @todo Remove this once the new recipe object is implemented.
		 * @deprecated Re-do tokens to use the new recipe object.
		 */
		this.tokens = new Tokens( 'action', 'text', { itemID: this.itemID } );
	}

	/**
	 * This is the primary function of the component. With the provided sentence and the fields, it creates the layout
	 * that renders each sentence segment with its value, including tokens and field values.
	 *
	 * It operates by processing the sentence into distinct segments, applying specific HTML markup to each segment
	 * based on its content, and assembling these marked-up segments into a complete HTML template for rendering.
	 *
	 * The sentence string is first split into an array of substrings (or segments) based on the {{ delimiter, which
	 * indicates the start of a "pill". The function then iterates over each segment, further dividing it based on
	 * the }} delimiter, which marks the end of the pill.
	 *
	 * If a segment doesn't contain a }}, it means it's a plain text segment, so the function wraps it with a span
	 * element and a sentence-plain CSS class.
	 *
	 * If a }} is found, it suggests the segment includes a pill. The function splits this segment by the : character
	 * to derive a IDs that will be used to retrieve the segment ID and the default text that makes up the pill's
	 * content when there isn't a value set. Depending on whether the segment is the first in the array, the function
	 * wraps the pill content and subsequent text differently to ensure correct spacing in the final output. The pill
	 * content gets a span element with a sentence-pill CSS class, while the subsequent text is treated as plain text.
	 *
	 * Finally, the function uses the html tagged template literal function to compile the processed segments into a
	 * complete HTML template. The template wraps all segments within a span element with a sentence CSS class.
	 *
	 * @return {TemplateResult} The sentence
	 */
	get _templateSentence() {
		// Decode the sentence. Lit will take care of the sanitization
		const sentence = htmlEntities.decode( this.sentence );

		// Divide the input into potential pill segments and plain text segments
		const segments = sentence.split( '{{' );

		return html`
			<span class="sentence sentence--standard">
				${
					// Transform each segment based on its content
					segments.map( segment => {
						// Check if the segment contains a pill by looking for "}}"
						const parts = segment.split( '}}' );

						// If the segment is plain text, wrap it with the appropriate span and class
						if ( parts.length === 1 ) {
							return this._templateSentencePlain( parts[0] );
						}

						// If the segment is a pill, extract the pill code and default content
						const [ pillDefaultText, pillCode, optionsCode ] = parts[0].split( ':' );

						return html`
							${ this._templateSentencePill( pillDefaultText, pillCode, optionsCode ) }
							${ this._templateSentencePlain( parts[1] ) }
						`;
					} )
				}
			</span>
		`;
	}

	/**
	 * Renders a plain segment
	 *
	 * @param {String} segmentContent The segment content (text of plain section)
	 * @return {TemplateResult|undefined} The segment if there is content, otherwise `undefined`
	 */
	_templateSentencePlain( segmentContent ) {
		if ( isEmpty( segmentContent ) ) {
			return;
		}

		return html`<span class="sentence-plain">${ segmentContent }</span>`;
	}

	/**
	 * Renders a pill and triggers an event when the user clicks on it
	 *
	 * @param {String} pillDefaultText The default text when there isn't a value defined
	 * @param {String} pillCode The code of the pill itself. This is what we'll use to get the value of the pill
	 * @param {String} optionsCode The code of the options group. If not defined, it'll be the same as the pill code
	 * @return {TemplateResult} The pill
	 */
	_templateSentencePill( pillDefaultText, pillCode, optionsCode = pillCode ) {
		// Gets the value of the pill. If there isn't a value available, it uses the default text.
		const pillValue = this._templateSentencePillValue( pillCode ) || pillDefaultText;

		// Check if the pill should be interactive
		if ( this.notInteractivePills ) {
			return html`
				<span class="sentence-pill">${ this._templateSentencePillLabel( pillCode ) }${ pillValue }</span>
			`;
		}

		return html`
			<uo-button 
				class="sentence-pill"
				size="small"
				?filled=${ this.doesPillHaveValue( pillCode ) }

				@click=${ () => {
					// Notify parents about the click to the sentence pill
					this.dispatchEvent(
						new CustomEvent(
							'uap-builder-sentence-pill-click',
							{
								detail: {
									pillCode: pillCode,
									optionsCode: optionsCode
								}
							}
						)
					);
				} }
			>${ this._templateSentencePillLabel( pillCode ) }${ pillValue }</uo-button>
		`;
	}

	/**
	 * Renders the inner value of a pill using the fields data. Returns `undefined` if there isn't a value available.
	 *
	 * @param {String} pillCode The code of the pill itself. This is what we'll use to get the value of the pill
	 * @return {TemplateResult|undefined} The pill value. `undefined` if it's not available
	 */
	_templateSentencePillValue( pillCode ) {
		// Check if there is a field linked
		if ( ! isDefined( this.fields[ pillCode ] ) ) {
			return;
		}

		// If there is a readable value, use that instead of the field value
		const printableValue = this.fields[ pillCode ].readable || this.fields[ pillCode ].value;

		return ! isEmpty( printableValue ) ?
			html`${ this.tokens.renderTextWithTokens( printableValue, { wrapperClass: 'sentence-pill-value' } ) }` :
			html`<span class="sentence-empty-value">(${ strings.empty })</span>`;
	}

	/**
	 * Renders the label of the field attached to the pill
	 *
	 * @param {String} pillCode The code of the pill itself. This is what we'll use to get the value of the pill
	 * @return {TemplateResult|undefined} The pill label. `undefined` if it's not available
	 */
	_templateSentencePillLabel( pillCode ) {
		// Check if there is a field linked
		if ( ! isDefined( this.fields[ pillCode ] ) || ! isDefined( this.fields[ pillCode ].backup ) ) {
			return;
		}

		if ( isEmpty( this.fields[ pillCode ].backup.label ) ) {
			return;
		}

		return html`<span class="sentence-pill-label">${ this.fields[ pillCode ].backup.label }: </span>`;
	}

	/**
	 * Checks if a given pill already has a value
	 *
	 * @param {String} pillCode The pill code
	 * @return {Boolean} Whether the pill has a value
	 */
	doesPillHaveValue( pillCode ) {
		return isDefined( this.fields[ pillCode ] );
	}

	/**
	 * Outputs the sentence as HTML string for the logs
	 *
	 * @return {String|undefined} The HTML string
	 */
	get outputForLogs() {
		if ( ! isDefined( this.$sentence ) ) {
			// If the sentence is not defined, return nothing
			return;
		}

		// Clone HTML on the shadow DOM
		let html = this.$sentence.cloneNode( true ).outerHTML;

		// Remove HTML comments
		// The regex /<!--[\s\S]*?-->/g matches HTML comments. <!-- starts a comment, [\s\S]*? matches any character including new lines (as many as possible, but as few as needed), and --> ends the comment.
		html = html.replace( /<!--[\s\S]*?-->/g, '' );

		if ( supportsRegExLookbehind() ) {
			// Remove spaces between HTML tags using lookbehind
			// The regex /(?<=>)\s+(?=<)/g uses lookbehind (?<=>) to assert what precedes is '>', and lookahead (?=<) to assert what follows is '<'.
			// \s+ matches one or more whitespace characters. This regex effectively removes whitespaces between HTML tags, but it may not be supported in older browsers.
			// Dynamically create the regex to avoid static analysis issues
			html = html.replace( ( new RegExp( '(?<=>)\\s+(?=<)', 'g' ) ), '' );
		} else {
			// Fallback for browsers that don't support lookbehind
			// The regex />\\s+</g matches '>' followed by any amount of whitespace, followed by '<'.
			// This is a simpler method to remove spaces between closing and opening HTML tags.
			html = html.replace( />\s+</g, '><' );
		}

		// Replace buttons with spans
		// The method replaceAll('uo-button', 'span') replaces all occurrences of 'uo-button' with 'span'.
		// This is used to change custom 'uo-button' elements into 'span' elements.
		return html.replaceAll( 'uo-button', 'span' );
	}
}
